import createServiceObservables from "./createServiceObservable";
import { v4 as uuid } from 'uuid';
import hashes$ from "./hashes";
import { getModule } from "vuex-module-decorators";
import PhotosModule from "@/store/modules/Photos";
import store from "@/store";
import Photo from "@/model/Photo";
import Jimp from "jimp";
import { merge, Subject } from 'rxjs';
import UploadedPhoto from "@/model/UploadedPhoto";
import { filter, map, share } from 'rxjs/operators';
import lifecycleSignal$ from './lifecycleSignal';
import compareImages from '../compareImages';
import zipForUpload from "./zipForUpload";
import errorsOnly from "./errorsOnly";
import largeThumbs$ from './largeThumbs';

let photosModule: PhotosModule;

const conflictCandidates = (sessionId: string, finalizationMode = false) => {
    if (!photosModule) photosModule = getModule(PhotosModule, store)
    return (photosModule.sessionPhotoIds[sessionId] || [])
        .map(id => [id, photosModule.remotePhotos[id]] as const)
        .filter(([, photo]) => !!photo)
        .filter(([ _, photo ]) => !finalizationMode || (photo.orderedBy && photo.orderedBy.length > 0))
        .map(([_, photo]) => photo);
};

type ConflictType = 'FILENAME'|'HASH';

const conflicts$ = new Subject<{
    photo: UploadedPhoto,
    resolve: (id?: string) => void,
    conflicted: Photo,
    conflictType: ConflictType,
}>();
const findHashConflict = (sessionId: string, finalizationMode: boolean, perceptualHash: string) => {
    const sessionPhotos = conflictCandidates(sessionId, finalizationMode);

    return sessionPhotos
        .filter(({ perceptualHash }) => !!perceptualHash)
        .map(photo => ({
            photo,
            similarity: Jimp.compareHashes(perceptualHash, photo.perceptualHash!)
        }))
        .sort((a, b) => a.similarity > b.similarity ? 1 : -1)
        .filter(({ similarity }) => similarity < 0.3 || finalizationMode)
        .shift()
        ?.photo;
};

const findFilenameConflict = (sessionId: string, filename: string) => {
    const sessionPhotos = conflictCandidates(sessionId);

    return sessionPhotos
        .find(({ filename: oFilename }) => filename === oFilename);
}

const resolveId = async (photo: UploadedPhoto, conflictType?: ConflictType, conflicted?: Photo | null) => {
    if (photo.id) {
        return { id: photo.id, overwrite: true };
    } else if (conflicted && conflictType) {
        const id = await new Promise<string | undefined>((resolve) => {
            const abort = () => finish();
            const finish = (id?: string) => {
                photo.signal.removeEventListener('abort', abort);
                resolve(id);
            };
            photo.signal.addEventListener('abort', abort, { once: true });
            if (!photo.active) { finish(); return; }
            conflicts$.next({
                photo,
                resolve: finish,
                conflicted,
                conflictType
            });
        });

        return id ? { id, overwrite: true } : { id: uuid(), overwrite: false };
    } else {
        return { id: uuid(), overwrite: false };
    }
}

const filenameConflictData$ = hashes$.pipe(
    map((hashes) => ({
        ...hashes,
        conflict: hashes.photo.id ? undefined : findFilenameConflict(hashes.photo.sessionId, hashes.photo.file.name),
    })),
    share(),
);

const filenameConflicts$ = filenameConflictData$.pipe(
    filter(({ conflict }) => !!conflict),
    map(data => ({ ...data, conflictType: 'FILENAME' }))
);

const hashConflictData$ = filenameConflictData$.pipe(
    filter(({ conflict }) => !conflict),
    map((hashes) => ({
        ...hashes,
        conflict: hashes.photo.id ? undefined : findHashConflict(hashes.photo.sessionId, hashes.photo.finalizationMode, hashes.perceptual)
    })),
    map(data => ({ ...data, ...(data.conflict ? { conflictType: 'HASH' } : {}) })),
    share(),
);

const falseHashConflicts$ = hashConflictData$.pipe(
    filter(({ conflict }) => !conflict)
);

const pixelChecks = createServiceObservables(
    'pixelConflicts',
    zipForUpload(
        { thumb: largeThumbs$, in: hashConflictData$ },
        ({ photo }) => photo,
        hashConflictData$.pipe(errorsOnly()),
        lifecycleSignal$.pipe(map(({ photo }) => photo)),
    ).pipe(filter(({ conflict }) => !!conflict)),
    async value => {
        const response = await fetch(value.conflict!.largeThumbUrl, {
            credentials: 'include', signal: value.photo.signal,
        });
        if (!response.ok) throw new Error(`Conflict thumbnail request failed: ${response.status}`);
        const pixelDiff = await compareImages(
            await value.largeThumb.arrayBuffer(), await response.arrayBuffer(), value.photo.signal,
        );
        return { ...value, conflict: pixelDiff < 0.3 || value.photo.finalizationMode ? value.conflict : null };
    },
);

const observables = createServiceObservables(
    'ids',
    merge(filenameConflicts$, falseHashConflicts$, pixelChecks.withoutErrors$),
    async (params) => ({
        ...params,
        ...await resolveId(
            params.photo,
            params.conflictType as ConflictType,
            params.conflict,
        )
    }),
    1000
);

const idErrors$ = merge(observables.errorsOnly$, pixelChecks.errorsOnly$);
const ids$ = observables.withoutErrors$;

export { idErrors$, conflicts$ };
export default ids$;
